import { authLogger } from "../../utils/logger";import { authService } from './authService';

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  tenantId: string
  firstName?: string;
  lastName?: string;
  initials?: string;
  useIcon?: boolean;
  company?: string;
  role?: string;
  refreshToken?: string;
}

export interface SessionInfo {
  isValid: boolean;
  user?: SessionUser;
  token?: string;
}

// Environment variables
const MOCK_MODE = import.meta.env.VITE_MOCK_MODE === 'true';

export const sessionService = {
  /**
   * Check if there's an existing valid session
   */
  async checkExistingSession(): Promise<SessionInfo> {
    if (MOCK_MODE) {
      // In mock mode, check localStorage for persisted auth state
      try {
        const stored = localStorage.getItem('fabrik28-app-store');
        if (stored) {
          const parsedStore = JSON.parse(stored);
          if (parsedStore.state?.isAuthenticated && parsedStore.state?.user) {
            // Check if firstName and lastName are N/A or Unavailable and set useIcon flag
            const userData = parsedStore.state.user;
            if (userData.firstName === 'N/A' || userData.lastName === 'N/A' || 
                userData.firstName === 'Unavailable' || userData.lastName === 'Unavailable') {
              userData.useIcon = true;
              userData.initials = ''; // Empty initials to force icon usage
              userData.name = 'Unknown'; // Set name to Unknown when both first and last names are default values
            }
            
            // Return the full user data that was persisted
            return {
              isValid: true,
              user: userData, // This preserves all user fields including initials
              token: 'mock-jwt-token'
            };
          }
        }
      } catch (error) {
        authLogger.error('Error checking mock session:', error);
      }
      
      return { isValid: false };
    }

    try {
      // Check if user is authenticated with Cognito
      const isAuth = await authService.isAuthenticated();
      
      if (isAuth) {
        // Get user details
        const user = await authService.getCurrentUser();
        const session = await authService.getCurrentSession();
        
        if (user && session) {
          // Extract user attributes (same logic as in authApi.login)
          const attributes = user?.attributes || {};
          authLogger.debug('🔍 SessionService: Extracted attributes:', attributes);
          
          const firstName = attributes.given_name || '';
          const lastName = attributes.family_name || '';
          const userEmail = attributes.email || user?.username || '';
          const tenantId = attributes['custom:tenantId'] || '';
          
          // Create full name
          const fullName = (firstName && lastName) ? `${firstName} ${lastName}` : '';
          const displayName = fullName || user?.username || userEmail;
          
          // Check if firstName and lastName are N/A or Unavailable
          const hasRealName = firstName !== 'N/A' && lastName !== 'N/A' && 
                             firstName !== 'Unavailable' && lastName !== 'Unavailable' &&
                             firstName !== '' && lastName !== '';
          
          // Generate initials from first and last name
          const getInitials = (): string => {
            if (firstName && lastName) {
              return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
            } else if (fullName) {
              const nameParts = fullName.split(' ').filter(part => part.length > 0);
              if (nameParts.length >= 2) {
                return `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase();
              } else if (nameParts.length === 1) {
                return nameParts[0].substring(0, 2).toUpperCase();
              }
            }
            // Fallback to email
            return userEmail.substring(0, 2).toUpperCase();
          };
          
          // Determine user role based on given_name and family_name (same logic as authApi)
          // If both are "N/A", assign "User" role, otherwise assign "Admin" role
          const userRole = (firstName === 'N/A' && lastName === 'N/A') ? 'User' : 'Admin';
          
          // Get refresh token
          const refreshToken = session.tokens?.refreshToken?.toString() || '';
          
          return {
            isValid: true,
            user: {
              id: user.userId || '',
              name: hasRealName ? displayName : 'Unknown', // Set name to Unknown when names are default values
              email: userEmail,
              tenantId: tenantId,
              firstName: firstName,
              lastName: lastName,
              initials: hasRealName ? getInitials() : '',
              useIcon: !hasRealName, // Use icon instead of initials when names are Unknown or Unavailable
              company: 'N/A',
              role: userRole, // Use the same role logic as authApi
              refreshToken: refreshToken
            },
            token: session.tokens?.idToken?.toString() || ''
          };
        }
      }
      
      return { isValid: false };
    } catch (error) {
      authLogger.error('Error checking existing session:', error);
      return { isValid: false };
    }
  },

  /**
   * Sign out and clear session
   */
  async signOut(): Promise<void> {
    try {
      await authService.signOut();
    } catch (error) {
      authLogger.error('Error signing out:', error);
      // Continue with local cleanup even if remote signout fails
    }
    
    // Clear persisted state
    try {
      localStorage.removeItem('fabrik28-app-store');
    } catch (error) {
      authLogger.error('Error clearing local storage:', error);
    }
  },

  /**
   * Get user attributes from Cognito (for future use)
   */
  async getUserAttributes(): Promise<Record<string, string>> {
    if (MOCK_MODE) {
      return {
        email: 'demo@fabrik28.ai',
        name: 'Demo User',
        firstName: 'Demo',
        lastName: 'User',
        company: 'Fabirk 28'
      };
    }

    try {
      const user = await authService.getCurrentUser();
      const attributes = user?.attributes || {};
      
      return {
        email: attributes.email || user?.username || '',
        name: user?.username || '',
        firstName: attributes.given_name || '',
        lastName: attributes.family_name || '',
        company: 'N/A',
        // Add more attributes as needed
      };
    } catch (error) {
      authLogger.error('Error getting user attributes:', error);
      return {};
    }
  }
};
